import {
  each,
  filter,
  groupBy,
  intersection,
  isEqual,
  keys,
  map,
  reduce,
  toLower,
  toUpper,
  values,
  without,
  cloneDeep,
} from "lodash";

import {
  ADD,
  LEVEL_TO_POSTCODE_KEY,
  LOWER_LEVELS,
  REMOVE,
} from "../consts/map";
import {
  COLLECTION_TAB_FOR_FIELD,
  COLLECTION_TAB_FOR_TYPE,
  DEFAULT_COLLECTION_AREA,
  DEFAULT_COLLECTION_TYPES,
  PLANNED,
  SAME_DAY,
} from "../consts/collections";
import { OUTER, SECTOR } from "../consts/layers";
import { POSTCODE } from "../consts/strings";
import { DEFAULT_PLAN_TYPE } from "../consts/planTypes";

import { Postcode } from "./postcode";
import { timestampToHHMM } from "./date";
import { groupPostcodes, groupSectors } from "./lookUpTables";
import {
  getRoutesPerTimeband,
  getRoutesForTimeband,
  processRoutes,
  routesAsObject,
} from "./routes";

export const loadCollectionsType = ({
  matrixProfile,
  routes,
  depotCollections,
  lookupTables,
}) => {
  const defaultCollectionAreas = getDefaultCollectionAreas({
    routes,
    matrixProfile,
    lookupTables,
  });

  const { collectionAreas, routes: routesWithAllocation } =
    extractCollectionAreas({
      collectionConfig: matrixProfile.collectionConfig,
      routes: processRoutes(routes),
      defaultCollectionAreas,
    });

  const routesByOuter = getRoutesForCollectionAreas({
    collectionAreas,
    lookupTables,
  });

  return {
    ...matrixProfile,
    routes: routesWithAllocation,
    routesByOuter,
    collectionAreas,
    depotCollections,
  };
};

export const getDefaultCollectionAreas = ({
  routes,
  matrixProfile,
  lookupTables,
}) => {
  const defaultRoutes = processRoutes(routes, matrixProfile.routeConfig);
  const routesPerTimeband = getRoutesPerTimeband({
    routes: defaultRoutes,
    planType: DEFAULT_PLAN_TYPE,
    collectionType: PLANNED,
  });

  const defaultCollectionAreas = {
    OUTER: {},
    SECTOR: {},
    POSTCODE: {},
  };

  processLevels({
    collectionAreas: defaultCollectionAreas,
    routes: routesPerTimeband["default"],
    type: SAME_DAY,
    lookupTables,
  });

  const routesOnFirstWave = getRoutesForTimeband(
    defaultRoutes,
    keys(routesPerTimeband).sort()[0],
    DEFAULT_PLAN_TYPE,
    PLANNED
  );
  const routeCodes = [
    ...new Set([
      ...map(values(routesOnFirstWave.routeOuter), (r) => r.routeCode),
      ...map(values(routesOnFirstWave.routeSector), (r) => r.routeCode),
      ...map(values(routesOnFirstWave.routePostcode), (r) => r.routeCode),
    ]),
  ];

  const routesObject = routesAsObject(defaultRoutes);
  const sortedFirstWaveRoutes = routeCodes
    .map((code) => routesObject[code])
    .sort(
      (
        { plannedReadyTime: plannedReadyTimeA },
        { plannedReadyTime: plannedReadyTimeB }
      ) =>
        (plannedReadyTimeB || "default").localeCompare(
          plannedReadyTimeA || "default"
        )
    );
  processLevels({
    collectionAreas: defaultCollectionAreas,
    routes: sortedFirstWaveRoutes,
    type: PLANNED,
    lookupTables,
  });
  return defaultCollectionAreas;
};

const processLevels = ({
  routes,
  collectionAreas,
  type = PLANNED,
  lookupTables,
}) => {
  const routesByLevel = reduce(
    routes,
    (prev, route) => {
      if (route.routeType !== DEFAULT_PLAN_TYPE) return prev;
      each(route.outer, (outer) => {
        prev.OUTER[outer.postcodeOuter] = route;
      });
      each(route.sector, (sector) => {
        prev.SECTOR[sector.postcodeSector] = route;
      });
      each(route.postcode, (postcode) => {
        prev.POSTCODE[postcode.postcode] = route;
      });
      return prev;
    },
    { OUTER: {}, SECTOR: {}, POSTCODE: {} }
  );

  each([OUTER, SECTOR, POSTCODE], (level) => {
    let groupedTables = level !== POSTCODE && {
      SECTOR: groupBy(keys(lookupTables.SECTOR), (key) =>
        level === OUTER ? Postcode.toOuter(key) : Postcode.toSector(key)
      ),
      POSTCODE: groupBy(keys(lookupTables.POSTCODE), (key) =>
        level === OUTER ? Postcode.toOuter(key) : Postcode.toSector(key)
      ),
    };
    each(keys(routesByLevel[level]), (area) => {
      collectionAreas[level][area] = {
        ...DEFAULT_COLLECTION_AREA,
        ...collectionAreas[level][area],
        [toLower(level)]: area,
        [DEFAULT_COLLECTION_TYPES[type].DEFAULT_ROUTE_CODE_KEY]:
          routesByLevel[level][area].routeCode,
        [DEFAULT_COLLECTION_TYPES[type].DEFAULT_COLOUR_KEY]:
          routesByLevel[level][area].colour,
      };

      propagateArea({
        collectionAreas,
        area,
        level,
        lowerLevels: LOWER_LEVELS[level],
        lookupTables: groupedTables,
        routesByArea: routesByLevel[level],
        type,
      });
    });
  });
};

const propagateArea = ({
  collectionAreas,
  area,
  level,
  lowerLevels = [SECTOR, POSTCODE],
  lookupTables,
  routesByArea,
  type = PLANNED,
}) => {
  each(lowerLevels, (lowerLevel) => {
    each(lookupTables[lowerLevel][area], (area) => {
      const postcode = new Postcode(area).getPostcode(level);
      collectionAreas[lowerLevel][area] = {
        ...DEFAULT_COLLECTION_AREA,
        ...collectionAreas[lowerLevel][area],
        [toLower(lowerLevel)]: area,
        [DEFAULT_COLLECTION_TYPES[type].DEFAULT_ROUTE_CODE_KEY]:
          routesByArea[postcode].routeCode,
        [DEFAULT_COLLECTION_TYPES[type].DEFAULT_COLOUR_KEY]:
          routesByArea[postcode].colour,
      };
    });
  });
};

export const extractCollectionAreas = ({
  collectionConfig,
  routes,
  defaultCollectionAreas,
}) => {
  const collectionAreas = defaultCollectionAreas ?? {
    OUTER: {},
    SECTOR: {},
    POSTCODE: {},
  };
  const routesByRouteCode = reduce(
    routes,
    (prev, next) => {
      prev[next.routeCode] = next;
      return prev;
    },
    {}
  );
  each(collectionConfig, (config) => {
    processCollectionAreasItem({
      allocations: config.outer,
      collectionAreas,
      areaKey: "outer",
      areaObjectKey: "postcodeOuter",
      routesObject: routesByRouteCode,
      next: (item) => {
        processCollectionAreasItem({
          allocations: item.sector,
          collectionAreas,
          areaKey: "sector",
          areaObjectKey: "postcodeSector",
          routesObject: routesByRouteCode,
        });
        processCollectionAreasItem({
          allocations: item.postcode,
          collectionAreas,
          areaKey: "postcode",
          areaObjectKey: "postcode",
          routesObject: routesByRouteCode,
        });
      },
    });
  });

  return { collectionAreas, routes: values(routesByRouteCode) };
};

const processCollectionAreasItem = ({
  allocations = [],
  collectionAreas = {},
  areaKey = "outer",
  areaObjectKey = "postcodeOuter",
  routesObject = {},
  next,
}) => {
  if (!allocations?.length) return;
  each(allocations, (item) => {
    if (item.plannedRouteCode) {
      routesObject[item.plannedRouteCode] = {
        ...routesObject[item.plannedRouteCode],
        [areaKey]: [
          ...routesObject[item.plannedRouteCode][areaKey],
          {
            [areaObjectKey]: item[toLower(areaKey)],
            collectionType: PLANNED,
          },
        ],
        plannedRoute: true,
      };
    }
    if (item.sameDayRouteCode) {
      routesObject[item.sameDayRouteCode] = {
        ...routesObject[item.sameDayRouteCode],
        [areaKey]: [
          ...routesObject[item.sameDayRouteCode][areaKey],
          {
            [areaObjectKey]: item[toLower(areaKey)],
            collectionType: SAME_DAY,
          },
        ],
        sameDayRoute: true,
      };
    }
    collectionAreas[toUpper(areaKey)][item[toLower(areaKey)]] = {
      [areaKey]: item[toLower(areaKey)],
      ...collectionAreas[toUpper(areaKey)][item[toLower(areaKey)]],
      earliestReadyTime: timestampToHHMM(item.earliestReadyTime),
      latestReadyTime: timestampToHHMM(item.latestReadyTime),
      sameDayCutOff: timestampToHHMM(item.sameDayCutOff),
      saturnAuto: item.saturnAuto == null ? null : !!item.saturnAuto,
      plannedRoute: item.plannedRouteCode,
      sameDayRoute: item.sameDayRouteCode,
    };

    next && next(item);
  });
};

export const processLowerLevelCollectionLabels = ({
  labels = {},
  level = OUTER,
  collectionAreas = [],
  lookupTable,
  depotDayValues,
}) => {
  if (!lookupTable) return;
  each(collectionAreas, (collection) => {
    if (
      !!!collection.earliestReadyTime &&
      !!!collection.latestReadyTime &&
      !!!collection.sameDayCutOff &&
      !!!collection.saturnAuto
    )
      return;

    const postcode = new Postcode(
      collection.postcode || collection.sector
    ).getPostcode(level);

    if (labels[postcode]?.collection) {
      const parentCollection = labels[postcode].collection;
      const difference =
        (collection.earliestReadyTime !== parentCollection.earliestReadyTime &&
          collection.earliestReadyTime !==
            depotDayValues.defaultEarliestReadyTime) ||
        (collection.latestReadyTime !== parentCollection.latestReadyTime &&
          collection.latestReadyTime !==
            depotDayValues.defaultLatestReadyTime) ||
        (collection.sameDayCutOff !== parentCollection.sameDayCutOff &&
          collection.sameDayCutOff !== depotDayValues.defaultSameDayCutOff) ||
        (collection.saturnAuto !== parentCollection.saturnAuto &&
          collection.saturnAuto !== depotDayValues.defaultSaturnAuto);
      if (difference) {
        labels[postcode] = {
          ...labels[postcode],
          postcode,
          mismatches: (labels[postcode]?.mismatches || 0) + 1,
        };
      }
    } else if (
      lookupTable[level][postcode].center &&
      (collection.earliestReadyTime !==
        depotDayValues.defaultEarliestReadyTime ||
        collection.latestReadyTime !== depotDayValues.defaultLatestReadyTime ||
        collection.sameDayCutOff !== depotDayValues.defaultSameDayCutOff ||
        collection.saturnAuto !== depotDayValues.defaultSaturnAuto)
    ) {
      labels[postcode] = {
        postcode,
        geometry: { coordinates: lookupTable[level][postcode].center },
        mismatches: (labels[postcode]?.mismatches || 0) + 1,
      };
    }
  });
};

const getCollectionAreasForVisiblePostcodes = ({
  collectionAreas = {},
  visiblePostcodes = [],
  visibleArtificialPostcodes = [],
  key = "sector",
  level = OUTER,
}) => {
  const grouped = groupBy(collectionAreas, (i) =>
    Postcode.getPostcode(i[key], level)
  );
  const areas = [];
  each(visiblePostcodes, (postcode) => {
    areas.push(...(grouped[postcode] || []));
  });
  each(visibleArtificialPostcodes, (postcode) => {
    areas.push(...(grouped[postcode] || []));
  });
  return areas;
};

export const getMismatchesForLabels = ({
  level,
  labels,
  collectionAreas,
  visiblePostcodes,
  visibleArtificialPostcodes,
  lookupTable,
  collectionDefaults,
}) => {
  if (level === OUTER) {
    processLowerLevelCollectionLabels({
      labels,
      level: OUTER,
      lookupTable,
      depotDayValues: collectionDefaults,
      collectionAreas: getCollectionAreasForVisiblePostcodes({
        collectionAreas: collectionAreas.SECTOR,
        visiblePostcodes,
        visibleArtificialPostcodes,
        key: "sector",
        level: OUTER,
      }),
    });
    processLowerLevelCollectionLabels({
      labels,
      level: OUTER,
      lookupTable,
      depotDayValues: collectionDefaults,
      collectionAreas: getCollectionAreasForVisiblePostcodes({
        collectionAreas: collectionAreas.POSTCODE,
        visiblePostcodes,
        visibleArtificialPostcodes,
        key: "postcode",
        level: OUTER,
      }),
    });
  }
  if (level === SECTOR) {
    processLowerLevelCollectionLabels({
      labels,
      level: SECTOR,
      lookupTable,
      depotDayValues: collectionDefaults,
      collectionAreas: getCollectionAreasForVisiblePostcodes({
        collectionAreas: collectionAreas.POSTCODE,
        visiblePostcodes,
        visibleArtificialPostcodes,
        key: "postcode",
        level: SECTOR,
      }),
    });
  }
};

export const applyChangeToCollections = ({
  routesImpacted,
  routes,
  preEditRoutes,
  routesTimes,
  preEditRoutesTimes,
  routesByOuter,
  collectionAreas,
  collectionSelectedTab,
  lookupTables,
  change,
}) => {
  routes = routes.map((route) =>
    change.impactRouteCodes.includes(route.routeCode) ? cloneDeep(route) : route
  );
  if (change.type === ADD)
    ({ routes, collectionAreas } = addCollectionToRoute({
      ...change,
      routes,
      collectionAreas,
      collectionSelectedTab,
      lookupTables,
      postcode: new Postcode(change.postcode),
    }));
  else if (change.type === REMOVE)
    ({ routes, collectionAreas } = removeCollectionFromRoute({
      ...change,
      routes,
      collectionAreas,
      collectionSelectedTab,
      postcode: new Postcode(change.postcode),
    }));
  else throw new Error("Unknown collection edit action");

  routesByOuter = applyChangeToRoutesForCollections({
    collectionAreas,
    routesByOuter,
    change,
  });

  ({ routesImpacted, routes } = restoreRoutesWithRevertedChanges({
    routesImpacted,
    routes,
    preEditRoutes,
    routesTimes,
    preEditRoutesTimes,
    change,
  }));

  return {
    routes,
    routesImpacted,
    routesByOuter,
    collectionAreas,
  };
};

const applyChangeToRoutesForCollections = ({
  collectionAreas,
  routesByOuter,
  change,
}) => {
  const { outer, sectors, postcodes } = change;
  routesByOuter = { ...routesByOuter };
  const plannedByOuter = new Set();
  const sameDayByOuter = new Set();
  routesByOuter[outer] = {
    [PLANNED]: plannedByOuter,
    [SAME_DAY]: sameDayByOuter,
  };
  const outerCollection = collectionAreas.OUTER[outer] || {};
  outerCollection?.plannedRoute &&
    plannedByOuter.add(outerCollection?.plannedRoute);
  outerCollection?.sameDayRoute &&
    sameDayByOuter.add(outerCollection?.sameDayRoute);

  sectors.forEach((sector) => {
    const sectorCollection = collectionAreas.SECTOR[sector] || {};
    sectorCollection?.plannedRoute &&
      plannedByOuter.add(sectorCollection?.plannedRoute);
    sectorCollection?.sameDayRoute &&
      sameDayByOuter.add(sectorCollection?.sameDayRoute);

    postcodes[sector].forEach((postcode) => {
      const postcodeCollection = collectionAreas.POSTCODE[postcode] || {};
      postcodeCollection?.plannedRoute &&
        plannedByOuter.add(postcodeCollection?.plannedRoute);
      postcodeCollection?.sameDayRoute &&
        sameDayByOuter.add(postcodeCollection?.sameDayRoute);
    });
  });
  return routesByOuter;
};

const addCollectionToRoute = ({
  routes,
  routeEditCode,
  collectionAreas,
  collectionSelectedTab,
  level,
  postcode,
  affectedRouteCode,
  lookupTables,
}) => {
  let postcodesToAllocate = null;
  const affected = routes.find(
    ({ routeCode }) => routeCode === affectedRouteCode
  );
  let modifiedCollectionAreas = {
    OUTER: { ...collectionAreas[OUTER] },
    SECTOR: { ...collectionAreas[SECTOR] },
    POSTCODE: { ...collectionAreas[POSTCODE] },
  };

  if (affected) {
    const {
      newRoute: newAffected,
      affectedPostcodes,
      newCollectionAreas,
    } = propagateAllocation({
      level,
      lookupTables,
      postcode,
      route: affected,
      allocate: false,
      collectionSelectedTab,
      collectionAreas: modifiedCollectionAreas,
    });
    newAffected[toLower(level)] = newAffected[toLower(level)].filter(
      ({ [LEVEL_TO_POSTCODE_KEY[level]]: levelPostcode, collectionType }) =>
        collectionType !== COLLECTION_TAB_FOR_TYPE[collectionSelectedTab] ||
        levelPostcode !== postcode.getPostcode(level)
    );
    newAffected[COLLECTION_TAB_FOR_FIELD[collectionSelectedTab]] =
      hasCollectionsOfType(newAffected, collectionSelectedTab);

    postcodesToAllocate = affectedPostcodes;
    routes.splice(
      routes.findIndex((item) => item.routeCode === newAffected.routeCode),
      1,
      newAffected
    );
    modifiedCollectionAreas = {
      ...modifiedCollectionAreas,
      ...newCollectionAreas,
    };
  } else {
    postcodesToAllocate = getPostcodesToAllocate({
      collectionAreas: modifiedCollectionAreas,
      level,
      lookupTables,
      postcode,
      affectedRoute: affected,
      collectionSelectedTab,
    });
  }

  const routeEdit = {
    ...routes.find(({ routeCode }) => routeCode === routeEditCode),
  };
  routeEdit[toLower(level)] = [
    ...routeEdit[toLower(level)],
    {
      [LEVEL_TO_POSTCODE_KEY[level]]: postcode.getPostcode(level),
      collectionType: COLLECTION_TAB_FOR_TYPE[collectionSelectedTab],
    },
  ];

  let { newRoute: newRouteEdit, newCollectionAreas } = propagateAllocation({
    level,
    postcodesToAllocate,
    lookupTables,
    postcode,
    route: routeEdit,
    allocate: true,
    collectionSelectedTab,
    collectionAreas: modifiedCollectionAreas,
  });

  const collection = modifiedCollectionAreas[level][postcode.getPostcode(level)]
    ? { ...modifiedCollectionAreas[level][postcode.getPostcode(level)] }
    : {
        ...DEFAULT_COLLECTION_AREA,
        [toLower(level)]: postcode.getPostcode(level),
      };

  collection[COLLECTION_TAB_FOR_FIELD[collectionSelectedTab]] =
    routeEdit.routeCode;
  modifiedCollectionAreas = {
    ...modifiedCollectionAreas,
    ...newCollectionAreas,
  };
  modifiedCollectionAreas[level][postcode.getPostcode(level)] = collection;

  newRouteEdit[COLLECTION_TAB_FOR_FIELD[collectionSelectedTab]] = true;

  let editIndex = routes.findIndex(
    (item) => item.routeCode === newRouteEdit.routeCode
  );
  routes.splice(editIndex, 1, newRouteEdit);

  return {
    routes,
    collectionAreas: modifiedCollectionAreas,
  };
};

const removeCollectionFromRoute = ({
  routes,
  routeEditCode,
  collectionAreas,
  collectionSelectedTab,
  level,
  postcode,
}) => {
  let modifiedCollectionAreas = {
    OUTER: { ...collectionAreas[OUTER] },
    SECTOR: { ...collectionAreas[SECTOR] },
    POSTCODE: { ...collectionAreas[POSTCODE] },
  };

  const routeEdit = {
    ...routes.find(({ routeCode }) => routeCode === routeEditCode),
  };
  const postcodesToAllocate = getPostcodesToAllocate({
    collectionAreas: modifiedCollectionAreas,
    level,
    lookupTables: {
      SECTOR: reduce(
        routeEdit.sector,
        (prev, next) => {
          prev[next.postcodeSector] = next;
          return prev;
        },
        {}
      ),
      POSTCODE: reduce(
        routeEdit.postcode,
        (prev, next) => {
          prev[next.postcode] = next;
          return prev;
        },
        {}
      ),
    },
    postcode,
    affectedRoute: routeEdit,
    collectionSelectedTab,
  });

  routeEdit[toLower(level)] = routeEdit[toLower(level)].filter(
    ({ [LEVEL_TO_POSTCODE_KEY[level]]: levelPostcode, collectionType }) =>
      collectionType !== COLLECTION_TAB_FOR_TYPE[collectionSelectedTab] ||
      levelPostcode !== postcode.getPostcode(level)
  );

  let { newRoute: newRouteEdit, newCollectionAreas } = propagateAllocation({
    level,
    postcodesToAllocate,
    postcode,
    route: routeEdit,
    allocate: false,
    collectionSelectedTab,
    collectionAreas: modifiedCollectionAreas,
  });
  newRouteEdit[COLLECTION_TAB_FOR_FIELD[collectionSelectedTab]] =
    hasCollectionsOfType(newRouteEdit, collectionSelectedTab);

  let editIndex = routes.findIndex(
    (item) => item.routeCode === newRouteEdit.routeCode
  );
  routes.splice(editIndex, 1, newRouteEdit);

  const collection = modifiedCollectionAreas[level][postcode.getPostcode(level)]
    ? { ...modifiedCollectionAreas[level][postcode.getPostcode(level)] }
    : {
        ...DEFAULT_COLLECTION_AREA,
        [toLower(level)]: postcode.getPostcode(level),
      };

  collection[COLLECTION_TAB_FOR_FIELD[collectionSelectedTab]] = null;
  modifiedCollectionAreas = {
    ...modifiedCollectionAreas,
    ...newCollectionAreas,
  };
  modifiedCollectionAreas[level][postcode.getPostcode(level)] = collection;

  return {
    routes,
    collectionAreas: modifiedCollectionAreas,
  };
};

const hasCollectionsOfType = (
  { outer, sector, postcode },
  collectionSelectedTab
) =>
  !![outer, sector, postcode].find((level) =>
    level.find(
      ({ collectionType }) =>
        collectionType === COLLECTION_TAB_FOR_TYPE[collectionSelectedTab]
    )
  );

const propagateAllocation = ({
  level,
  route,
  allocate = true,
  lookupTables,
  postcodesToAllocate,
  postcode,
  collectionSelectedTab,
  collectionAreas,
}) => {
  const newRoute = { ...route };
  const affectedPostcodes = { SECTOR: [], POSTCODE: [] };
  const newCollectionAreas = {};
  each(LOWER_LEVELS[level], (lowerLevel) => {
    const {
      collectionAreas: newCollectionAreasForLevel,
      affectedPostcodes: affectedPostcodesForLevel,
    } = propagateAllocationForLevel({
      level: lowerLevel,
      route: newRoute,
      allocate,
      collectionType: COLLECTION_TAB_FOR_FIELD[collectionSelectedTab],
      collectionAreas: collectionAreas[lowerLevel],
      collectionSelectedTab,
      postcodes: postcodesToAllocate
        ? postcodesToAllocate[lowerLevel]
        : reduce(
            keys(lookupTables[lowerLevel]),
            (prev, key) => {
              const p = new Postcode(key, true);
              if (p.getPostcode(level) === postcode.getPostcode(level)) {
                prev.push(key);
              }
              return prev;
            },
            []
          ),
    });
    affectedPostcodes[lowerLevel] = affectedPostcodesForLevel;
    newCollectionAreas[lowerLevel] = newCollectionAreasForLevel;
  });
  return { newRoute, affectedPostcodes, newCollectionAreas };
};

const propagateAllocationForLevel = ({
  level,
  route,
  postcodes = [],
  allocate = true,
  collectionAreas,
  collectionType,
  collectionSelectedTab,
}) => {
  if (allocate) {
    route[toLower(level)] = [
      ...route[toLower(level)],
      ...postcodes.map((postcode) => ({
        [LEVEL_TO_POSTCODE_KEY[level]]: postcode,
        collectionType: COLLECTION_TAB_FOR_TYPE[collectionSelectedTab],
      })),
    ];
    const newCollectionAreas = {};
    each(postcodes, (postcode) => {
      const collection = collectionAreas[postcode]
        ? { ...collectionAreas[postcode] }
        : {
            ...DEFAULT_COLLECTION_AREA,
            [toLower(level)]: postcode,
          };
      collection[collectionType] = route.routeCode;
      newCollectionAreas[postcode] = collection;
    });
    const unchangedCollections = without(keys(collectionAreas), ...postcodes);
    each(unchangedCollections, (postcode) => {
      newCollectionAreas[postcode] = collectionAreas[postcode];
    });
    return { collectionAreas: newCollectionAreas };
  } else {
    const affectedPostcodes = intersection(
      postcodes,
      route[toLower(level)].map((i) => i[LEVEL_TO_POSTCODE_KEY[level]])
    );
    route[toLower(level)] = route[toLower(level)].filter(
      ({ [LEVEL_TO_POSTCODE_KEY[level]]: postcode, collectionType }) =>
        collectionType !== COLLECTION_TAB_FOR_TYPE[collectionSelectedTab] ||
        !affectedPostcodes.includes(postcode)
    );

    const newCollectionAreas = {};
    each(postcodes, (postcode) => {
      const collection = collectionAreas[postcode]
        ? { ...collectionAreas[postcode] }
        : {
            ...DEFAULT_COLLECTION_AREA,
            [toLower(level)]: postcode,
          };
      collection[collectionType] = null;
      newCollectionAreas[postcode] = collection;
    });
    const unchangedCollections = without(keys(collectionAreas), ...postcodes);
    each(unchangedCollections, (postcode) => {
      newCollectionAreas[postcode] = collectionAreas[postcode];
    });
    return { collectionAreas: newCollectionAreas, affectedPostcodes };
  }
};

const getPostcodesToAllocate = ({
  collectionAreas,
  level,
  lookupTables,
  postcode,
  affectedRoute,
  collectionSelectedTab,
}) => {
  const affectedPostcodes = { SECTOR: [], POSTCODE: [] };
  each(LOWER_LEVELS[level], (lowerLevel) => {
    const allPostcodes = filter(keys(lookupTables[lowerLevel]), (key) => {
      const keyPostcode = new Postcode(key);
      return keyPostcode.getPostcode(level) === postcode.getPostcode(level);
    });
    const collectionAreasToCheck = intersection(
      keys(collectionAreas[lowerLevel]).filter((key) => {
        const keyPostcode = new Postcode(key);
        return keyPostcode.getPostcode(level) === postcode.getPostcode(level);
      }),
      allPostcodes
    );
    affectedPostcodes[lowerLevel] = without(
      allPostcodes,
      ...collectionAreasToCheck
    );
    each(collectionAreasToCheck, (postcode) => {
      const collectionRoute =
        collectionAreas[lowerLevel][postcode][
          COLLECTION_TAB_FOR_FIELD[collectionSelectedTab]
        ];
      if (!collectionRoute || collectionRoute === affectedRoute?.routeCode) {
        affectedPostcodes[lowerLevel].push(postcode);
      }
    });
  });
  return affectedPostcodes;
};

export const handleCollectionTimeEdits = ({
  newCollection,
  collectionAreas,
  depotDefaults,
  lookupTables,
}) => {
  collectionAreas = {
    OUTER: { ...collectionAreas.OUTER },
    SECTOR: { ...collectionAreas.SECTOR },
    POSTCODE: { ...collectionAreas.POSTCODE },
  };

  if (!newCollection?.postcode) return collectionAreas;

  const postcode = new Postcode(newCollection.postcode);
  const level = postcode.getLevel();
  const currentCollection = collectionAreas[level][
    postcode.getPostcode(level)
  ] || {
    [toLower(level)]: postcode.getPostcode(level),
    ...DEFAULT_COLLECTION_AREA,
  };

  const collectionChanges = {
    earliestReadyTime: null,
    latestReadyTime: null,
    sameDayCutOff: null,
    saturnAuto: null,
  };

  if (
    newCollection.earliestReadyTime !== depotDefaults.defaultEarliestReadyTime
  )
    collectionChanges.earliestReadyTime = newCollection.earliestReadyTime;
  if (newCollection.latestReadyTime !== depotDefaults.defaultLatestReadyTime)
    collectionChanges.latestReadyTime = newCollection.latestReadyTime;
  if (newCollection.sameDayCutOff !== depotDefaults.defaultSameDayCutOff)
    collectionChanges.sameDayCutOff = newCollection.sameDayCutOff;
  if (newCollection.saturnAuto !== depotDefaults.defaultSaturnAuto)
    collectionChanges.saturnAuto = newCollection.saturnAuto;

  propagateTimeEdits({
    collectionAreas,
    currentCollection,
    collectionChanges,
    level,
    lookupTables,
  });

  collectionAreas[level][postcode.getPostcode(level)] = {
    ...currentCollection,
    ...collectionChanges,
  };

  return collectionAreas;
};

const propagateTimeEdits = ({
  collectionAreas,
  currentCollection,
  collectionChanges,
  level,
  lookupTables,
}) => {
  if (level === OUTER) {
    const sectors =
      groupSectors(lookupTables?.SECTOR)[currentCollection.outer] || [];
    sectors.forEach((sector) => {
      const sectorCollection = {
        ...(collectionAreas.SECTOR[sector] || DEFAULT_COLLECTION_AREA),
        sector,
      };
      Object.keys(collectionChanges).forEach((key) => {
        if (currentCollection[key] !== sectorCollection[key]) return;
        sectorCollection[key] = collectionChanges[key];
      });
      collectionAreas.SECTOR[sector] = sectorCollection;

      propagateTimeEdits({
        collectionAreas,
        currentCollection: {
          ...currentCollection,
          sector,
        },
        collectionChanges,
        level: SECTOR,
        lookupTables,
      });
    });
  } else if (level === SECTOR) {
    const postcodes =
      groupPostcodes(lookupTables?.POSTCODE)[currentCollection.sector] || [];
    postcodes.forEach((postcode) => {
      const postcodeCollection = {
        ...(collectionAreas.POSTCODE[postcode] || DEFAULT_COLLECTION_AREA),
        postcode,
      };
      Object.keys(collectionChanges).forEach((key) => {
        if (currentCollection[key] !== postcodeCollection[key]) return;
        postcodeCollection[key] = collectionChanges[key];
      });
      collectionAreas.POSTCODE[postcode] = postcodeCollection;
    });
  }
};

export const setCollectionRoute = ({
  collectionAreasForLevel,
  collectionType = PLANNED,
  defaults = false,
}) => {
  const collectionArea = {};
  each(keys(collectionAreasForLevel), (key) => {
    collectionArea[key] = {
      ...collectionAreasForLevel[key],
      routeCode: defaults
        ? collectionAreasForLevel[key][
            DEFAULT_COLLECTION_TYPES[collectionType].ROUTE_CODE_KEY
          ] ||
          collectionAreasForLevel[key][
            DEFAULT_COLLECTION_TYPES[collectionType].DEFAULT_ROUTE_CODE_KEY
          ]
        : collectionAreasForLevel[key][
            DEFAULT_COLLECTION_TYPES[collectionType].ROUTE_CODE_KEY
          ],
    };
  });
  return collectionArea;
};

export const getRoutesForCollectionAreas = ({
  collectionAreas,
  lookupTables,
}) => {
  const routesByOuter = {};
  const outers = Object.keys(lookupTables.OUTER);
  outers.forEach((outer) => {
    const { plannedRoute, sameDayRoute } = collectionAreas.OUTER[outer] || {};
    routesByOuter[outer] = {
      [SAME_DAY]: new Set(),
      [PLANNED]: new Set(),
    };

    if (sameDayRoute) routesByOuter[outer][SAME_DAY].add(sameDayRoute);
    if (plannedRoute) routesByOuter[outer][PLANNED].add(plannedRoute);

    const sectors = groupSectors(lookupTables.SECTOR)[outer];
    sectors.forEach((sector) => {
      const { plannedRoute, sameDayRoute } =
        collectionAreas.SECTOR[sector] || {};
      if (sameDayRoute) routesByOuter[outer][SAME_DAY].add(sameDayRoute);
      if (plannedRoute) routesByOuter[outer][PLANNED].add(plannedRoute);

      const postcodes = groupPostcodes(lookupTables.POSTCODE)[sector];
      postcodes.forEach((postcode) => {
        const { plannedRoute, sameDayRoute } =
          collectionAreas.POSTCODE[postcode] || {};
        if (sameDayRoute) routesByOuter[outer][SAME_DAY].add(sameDayRoute);
        if (plannedRoute) routesByOuter[outer][PLANNED].add(plannedRoute);
      });
    });
  });

  return routesByOuter;
};

export const restoreRoutesWithRevertedChanges = ({
  routes,
  preEditRoutes,
  routesImpacted,
  change,
}) => {
  routesImpacted = { ...routesImpacted };
  change.impactRouteCodes.forEach((routeCode) => {
    const routeBeforeEdit = preEditRoutes.find(
      (route) => route.routeCode === routeCode
    );
    const routeAfterEditIndex = routes.findIndex(
      (route) => route.routeCode === routeCode
    );
    const routeAfterEdit = routes[routeAfterEditIndex];

    const hasEqualCollections = isEqual(
      getCollectionAllocationsMap(
        routeBeforeEdit,
        change.collectionSelectedTab
      ),
      getCollectionAllocationsMap(routeAfterEdit, change.collectionSelectedTab)
    );

    if (!hasEqualCollections) {
      routesImpacted[routeCode] = { optimised: false };
    } else {
      delete routesImpacted[routeCode];
      routes[routeAfterEditIndex] = routeBeforeEdit;
    }
  });

  return {
    routes,
    routesImpacted,
  };
};

const getCollectionAllocationsMap = (
  { outer, sector, postcode },
  collectionSelectedTab
) => {
  const acc = {};
  outer.forEach(({ postcodeOuter, collectionType }) => {
    if (collectionType === COLLECTION_TAB_FOR_TYPE[collectionSelectedTab])
      acc[postcodeOuter] = true;
  });
  sector.forEach(({ postcodeSector, collectionType }) => {
    if (collectionType === COLLECTION_TAB_FOR_TYPE[collectionSelectedTab])
      acc[postcodeSector] = true;
  });
  postcode.forEach(({ postcode, collectionType }) => {
    if (collectionType === COLLECTION_TAB_FOR_TYPE[collectionSelectedTab])
      acc[postcode] = true;
  });
  return acc;
};
